import { AlertCircle, Clock, XCircle, RefreshCw } from "lucide-react";
import type { PaymentStatus } from "@/features/learning/api/learningApi";

interface PaymentStatusBannerProps {
  status: PaymentStatus;
  message?: string | null;
  holdReleaseDate?: string | null;
}

const statusConfig: Record<
  PaymentStatus,
  {
    icon: React.ReactNode;
    bgClass: string;
    textClass: string;
    borderClass: string;
  }
> = {
  COMPLETED: {
    icon: null,
    bgClass: "",
    textClass: "",
    borderClass: "",
  },
  HELD: {
    icon: <Clock className="h-4 w-4" />,
    bgClass: "bg-amber-50",
    textClass: "text-amber-800",
    borderClass: "border-amber-200",
  },
  PENDING: {
    icon: <RefreshCw className="h-4 w-4 animate-spin" />,
    bgClass: "bg-blue-50",
    textClass: "text-blue-800",
    borderClass: "border-blue-200",
  },
  FAILED: {
    icon: <XCircle className="h-4 w-4" />,
    bgClass: "bg-red-50",
    textClass: "text-red-800",
    borderClass: "border-red-200",
  },
  REFUNDED: {
    icon: <AlertCircle className="h-4 w-4" />,
    bgClass: "bg-gray-50",
    textClass: "text-gray-800",
    borderClass: "border-gray-200",
  },
};

export function PaymentStatusBanner({
  status,
  message,
  holdReleaseDate,
}: PaymentStatusBannerProps) {
  // No mostrar banner para pagos completados
  if (status === "COMPLETED") {
    return null;
  }

  const config = statusConfig[status];

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("es-ES", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${config.bgClass} ${config.textClass} ${config.borderClass}`}
    >
      {config.icon}
      <div className="flex-1">
        <p className="text-sm font-medium">
          {message || `Estado del pago: ${status}`}
        </p>
        {status === "HELD" && holdReleaseDate && (
          <p className="text-xs mt-0.5 opacity-80">
            Fecha estimada de liberación: {formatDate(holdReleaseDate)}
          </p>
        )}
      </div>
    </div>
  );
}
